import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import FormattedMessage from 'I18n/components/FormattedMessage';
import Raven from 'raven-js';
import { OPEN_PAYMENT_POPOVER } from 'rich-text-lib/constants/usageTracking';
import InsertMenu from './InsertMenu';
import editorCapabilities, { isCustomCapability } from '../../editorCapabilities';
import InsertVideo from '../../commands/InsertVideo';
import { Tracker } from '../../../utils/usageTracker';
import { PM_PAYMENT } from '../../constants/events';
import useDebouncedEditorStateContext from '../../contexts/useDebouncedEditorStateContext';
import { insertSignature } from '../../utils/documentUtils';
import { findNode } from '../../utils/nodeUtils';
import { Dropdown } from 'foundations-components/transitional/components/Dropdown';
import { useEditorUIContext } from '../../contexts/EditorUIContext';
import { VideoModalTypes } from '../../../draft-transplant/GoVideoDialog';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
export default function InsertMenuContainer({
  editorConfig,
  insertTypeOrder,
  onOpen
}) {
  const {
    editorState,
    setEditorState
  } = useDebouncedEditorStateContext();
  // Ref allows handleSelectType to access current editorState without depending on it.
  // This prevents callback recreation on editor state changes, which would cause
  // Dropdown to remount its content and reset the typeahead loading state. See #323.
  const editorStateRef = useRef(editorState);
  useLayoutEffect(() => {
    editorStateRef.current = editorState;
  }, [editorState]);
  const [open, setOpen] = useState(false);
  const [selectedType, setSelectedType] = useState('');
  const {
    handleFirstClick
  } = useEditorUIContext();
  const [modalType, setModalType] = useState('');
  useEffect(() => {
    if (!open) {
      setSelectedType('');
    }
  }, [open]);
  const handleViewInsertMenu = useCallback(e => {
    e.stopPropagation();
    setSelectedType('');
  }, [setSelectedType]);
  const handleSelectType = useCallback(type => {
    setSelectedType(type);
    handleFirstClick === null || handleFirstClick === void 0 || handleFirstClick(`InsertMenu: ${type}`);
    switch (type) {
      case editorCapabilities.PAYMENT_LINK:
        Tracker.track(PM_PAYMENT, {
          action: OPEN_PAYMENT_POPOVER
        });
        break;
      case editorCapabilities.SIGNATURE:
        {
          setOpen(false);
          if (!editorConfig.SIGNATURE) {
            Raven.captureException(new Error(`SELECTED_TYPE_UNKNOWN`), {
              extra: {
                type
              }
            });
            break;
          }
          if (editorConfig.SIGNATURE.signature && !findNode(editorStateRef.current.doc, n => n.type.name === 'signature')) {
            const newState = insertSignature({
              htmlBody: editorConfig.SIGNATURE.signature,
              editorState: editorStateRef.current
            });
            setEditorState(newState);
          } else {
            editorConfig.SIGNATURE.showSignatureEditor();
          }
          break;
        }
      default:
        break;
    }
  }, [editorConfig.SIGNATURE, setEditorState, handleFirstClick]);
  const handleOpenChange = useCallback(({
    target: {
      value
    }
  }) => {
    setOpen(value);
    if (value && onOpen) {
      onOpen();
    }
  }, [setOpen, onOpen]);
  const handleOpenModal = useCallback(type => {
    setOpen(false);
    setModalType(type);
  }, [setModalType]);
  const handleCloseModal = useCallback(() => {
    setSelectedType('');
    setModalType('');
  }, [setSelectedType, setModalType]);
  const handleInsertComplete = useCallback(() => {
    setOpen(false);
  }, [setOpen]);
  const renderContent = useCallback(() =>
  /*#__PURE__*/
  // This is to prevent unexpected dropdown closing in tests
  _jsx("div", {
    onKeyDown: e => e.stopPropagation(),
    children: /*#__PURE__*/_jsx(InsertMenu, {
      editorConfig: editorConfig,
      insertTypeOrder: insertTypeOrder
      // @ts-expect-error temp for migrating selectedType from string to InsertCapability
      ,
      selectedType: selectedType,
      handleSelectType: handleSelectType,
      handleViewInsertMenu: handleViewInsertMenu,
      handleInsertComplete: handleInsertComplete,
      handleOpenModal: handleOpenModal
    })
  }), [editorConfig, insertTypeOrder, selectedType, handleSelectType, handleViewInsertMenu, handleInsertComplete, handleOpenModal]);
  const renderModal = () => {
    const shouldShowVideoModal = selectedType === editorCapabilities.VIDEO_INSERTION || [VideoModalTypes.INSERT_VIDEO_HUBSPOT, VideoModalTypes.INSERT_VIDEO_VIDYARD].includes(modalType);
    if (shouldShowVideoModal && editorConfig.VIDEO_INSERTION) {
      const entry = editorConfig.VIDEO_INSERTION;
      return /*#__PURE__*/_jsx(InsertVideo, {
        insertWithThumbnail: entry.insertWithThumbnail,
        insertWithEmbed: entry.insertWithEmbed,
        objectType: entry.objectType,
        recipientEmail: entry.recipientEmail,
        closeModal: handleCloseModal,
        onInsertComplete: handleInsertComplete,
        useInsertMenuVersion: true,
        modalType: modalType
      });
    }
    return null;
  };
  return /*#__PURE__*/_jsxs("div", {
    children: [/*#__PURE__*/_jsx(Dropdown, {
      buttonUse: "transparent",
      closeOnMenuClick: false,
      buttonText: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "hubspot-prosemirror.buttons.insertMenu"
      }),
      menuWidth: isCustomCapability(selectedType) && editorConfig[selectedType] && editorConfig[selectedType].menuWidth ? editorConfig[selectedType].menuWidth : 400,
      placement: "top right",
      open: open,
      onOpenChange: handleOpenChange,
      Content: renderContent,
      "data-test-id": "insert-menu-dropdown"
    }), renderModal()]
  });
}